import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';

const toggleEndpoint = apiUrl + 'pr/enablePRBot/';
const updateForOrgEndpoint = apiUrl + 'pr/updatePRBotSettingsForOrg/';

export interface UpdatePRBotSettingsPayload {
  repoName: string;
  orgUUID: string;
  /** When omitted with other flags, unchanged; when only repoName+orgUUID, toggles PR evaluation */
  enable_pr_evaluation?: boolean;
  enable_auto_comments?: boolean;
  enable_documentation?: boolean;
  enable_security?: boolean;
}

export interface TogglePRBotEnabledResponse {
  success: boolean;
  enable_pr_evaluation: boolean;
  enable_auto_comments?: boolean;
  enable_documentation?: boolean;
  enable_security?: boolean;
  error?: string | null;
}

/**
 * Update PR bot installation flags. When only repoName and orgUUID are passed, toggles enable_pr_evaluation (backward compat).
 * When any of enable_pr_evaluation, enable_auto_comments, enable_documentation, enable_security is passed, sets that field.
 */
export const togglePRBotEnabled = async (
  payload: UpdatePRBotSettingsPayload
): Promise<TogglePRBotEnabledResponse> => {
  const body: Record<string, unknown> = {
    repoName: payload.repoName,
    orgUUID: payload.orgUUID,
  };
  if (payload.enable_pr_evaluation !== undefined)
    body.enable_pr_evaluation = payload.enable_pr_evaluation;
  if (payload.enable_auto_comments !== undefined)
    body.enable_auto_comments = payload.enable_auto_comments;
  if (payload.enable_documentation !== undefined)
    body.enable_documentation = payload.enable_documentation;
  if (payload.enable_security !== undefined)
    body.enable_security = payload.enable_security;

  try {
    const response = await postWithAuth(toggleEndpoint, body);
    if (!response.ok) {
      throw new Error('Failed to update PR bot settings');
    }

    const data = await response.json();
    if (typeof data === 'string') {
      return JSON.parse(data);
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return data;
  } catch (error) {
    console.error(error);
    return {
      success: false,
      enable_pr_evaluation: false,
      error: 'Failed to update PR bot settings',
    };
  }
};

export interface UpdatePRBotSettingsForOrgPayload {
  orgUUID: string;
  enable_pr_evaluation?: boolean;
  enable_auto_comments?: boolean;
  enable_documentation?: boolean;
  enable_security?: boolean;
  enable_dedup?: boolean;
  auto_approve_on_safe?: boolean;
  /**
   * Scope, narrowest wins. `repo_names` is an explicit set; `git_org` and
   * `search` mirror the listing's own filters so the repos changed are exactly
   * the repos the user was looking at. All three omitted means every repo in
   * the org that has a PR bot installation.
   */
  repo_names?: string[];
  git_org?: string;
  search?: string;
  /** Count what would change without writing, so a confirm can name the number. */
  dry_run?: boolean;
}

export interface UpdatePRBotSettingsForOrgResponse {
  success: boolean;
  updated_count: number;
  error?: string | null;
  /** Rows the scope matched. On a dry run this is the preview count. */
  matched_count: number;
}

/**
 * Set PR bot flags across many repos in one request.
 *
 * The per-repo toggle is one call each over a listing that pages 20 at a time,
 * so resetting a large org meant thousands of requests. This applies the same
 * change server-side in a single statement.
 */
export const updatePRBotSettingsForOrg = async (
  payload: UpdatePRBotSettingsForOrgPayload
): Promise<UpdatePRBotSettingsForOrgResponse> => {
  const body: Record<string, unknown> = { orgUUID: payload.orgUUID };
  if (payload.enable_pr_evaluation !== undefined)
    body.enable_pr_evaluation = payload.enable_pr_evaluation;
  if (payload.enable_auto_comments !== undefined)
    body.enable_auto_comments = payload.enable_auto_comments;
  if (payload.enable_documentation !== undefined)
    body.enable_documentation = payload.enable_documentation;
  if (payload.enable_security !== undefined)
    body.enable_security = payload.enable_security;
  if (payload.enable_dedup !== undefined)
    body.enable_dedup = payload.enable_dedup;
  if (payload.auto_approve_on_safe !== undefined)
    body.auto_approve_on_safe = payload.auto_approve_on_safe;
  if (payload.repo_names !== undefined) body.repo_names = payload.repo_names;
  if (payload.git_org) body.git_org = payload.git_org;
  if (payload.search) body.search = payload.search;
  if (payload.dry_run) body.dry_run = true;

  try {
    const response = await postWithAuth(updateForOrgEndpoint, body);
    if (!response.ok) {
      throw new Error('Failed to update PR bot settings for org');
    }

    const data = await response.json();
    if (typeof data === 'string') {
      return JSON.parse(data);
    }

    return {
      success: data?.success ?? false,
      updated_count: data?.updated_count ?? 0,
      // Older backends don't send matched_count; fall back to updated_count so
      // a confirm never shows 0 against a build that did the work.
      matched_count: data?.matched_count ?? data?.updated_count ?? 0,
      error: data?.error ?? null,
    };
  } catch (error) {
    console.error(error);
    return {
      success: false,
      updated_count: 0,
      matched_count: 0,
      error: 'Failed to update PR bot settings for org',
    };
  }
};
